/**
 * Storage and engine names, said in words — for any text the server wrote.
 *
 * Owner's rule (2026-10-15): **nothing on screen says `.arrow`, and nothing
 * names the dataframe engine.** Those are how the data is kept, not what it
 * is, and they leak in through text the server composes for a person to read:
 * the Data health file list, the stage notes in both Timing panels, an error
 * message. The server's strings stay as they are — Data health matches a file
 * across machines by its path, and the logs want the real name — so the words
 * are chosen here, at the last moment, and every surface that shows such text
 * passes it through `plainText`.
 *
 * `fundamentals/earnings_next.arrow` reads `fundamentals › earnings next`.
 */

/** A path ending `.arrow`: letters, digits, `_ - . /`, no spaces. */
const FILE = /[A-Za-z0-9_\-./^]*[A-Za-z0-9_^-]\.arrow\b/g;

/**
 * The engine's exception names, with or without their module, and the colon
 * after them — `ComputeError: x` reads `x`. Listed rather than matched by
 * suffix, so a word of ours ending `Error` is never touched.
 */
const ENGINE_ERROR = new RegExp(
  "\\b(?:polars\\.exceptions\\.|pyarrow\\.lib\\.)?(?:" +
    [
      "ComputeError",
      "ColumnNotFoundError",
      "SchemaError",
      "SchemaFieldNotFoundError",
      "ShapeError",
      "InvalidOperationError",
      "DuplicateError",
      "NoDataError",
      "OutOfBoundsError",
      "StructFieldNotFoundError",
      "SQLInterfaceError",
      "SQLSyntaxError",
      "PolarsError",
      "PolarsPanicError",
      "PanicException",
      "ArrowError",
      "ArrowInvalid",
      "ArrowIOError",
      "ArrowTypeError",
    ].join("|") +
    ")\\b:?\\s*",
  "g",
);

/** The engine itself, by name. */
const ENGINE_WORD = /\b(?:polars|pyarrow)\b/gi;

/** One stored file's name, in words: folders become ` › `, `_` a space. */
export function plainFileName(path: string): string {
  return path
    .replace(/\.arrow$/, "")
    .split("/")
    .filter((part) => part !== "")
    .map((part) => part.replace(/_/g, " "))
    .join(" › ");
}

/** Any server-written text, with every file and engine name put in words. */
export function plainText(text: string): string;
export function plainText(text: string | null | undefined): string | undefined;
export function plainText(text: string | null | undefined): string | undefined {
  if (text == null) return undefined;
  return text
    .replace(FILE, (match) => plainFileName(match))
    .replace(ENGINE_ERROR, "")
    .replace(ENGINE_WORD, "data engine");
}
